import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Button,
  CustomSelect,
  Input,
  SideDrawer,
  Switch,
  SkeletonText,
  Textarea,
} from '@/components';
import { parseApiError } from '@/common/utils';
import { getWhatsAppSettings, updateWhatsAppSettings } from '../../api';
import { getCRMPipelineStages } from '../../crm/api';
import { usePermissions } from '@/common/hooks/usePermissions';
import { PERMISSIONS } from '@/common/config/permissions';

const COOLDOWN_HOUR_OPTIONS = [1, 2, 3, 4, 5, 6].map((h) => ({
  value: h,
  label: `${h} hour${h > 1 ? 's' : ''}`,
}));

const REMINDER_DELAY_PRESETS = [2, 5, 10, 15, 30, 45, 60];
const REMINDER_DELAY_OPTIONS = [
  ...REMINDER_DELAY_PRESETS.map((m) => ({
    value: m,
    label: `${m} min`,
  })),
  { value: 'custom', label: 'Custom' },
];

const MAIN_MENU_DELAY_PRESETS = [1, 5, 15, 30, 60];
const MAIN_MENU_DELAY_OPTIONS = [
  ...MAIN_MENU_DELAY_PRESETS.map((m) => ({
    value: m,
    label: `${m} minute${m > 1 ? 's' : ''}`,
  })),
  { value: 'custom', label: 'Custom' },
];

const SECTIONS = [
  {
    id: 'messages',
    title: 'Default Messages',
    description: 'Greeting for one-to-one chat',
    icon: 'message',
  },
  {
    id: 'flow',
    title: 'Flow Configuration',
    description: 'Menu, delay, and buttons',
    icon: 'flow',
  },
  {
    id: 'reminder',
    title: 'No-reply reminder',
    description: 'One nudge for every flow',
    icon: 'bell',
  },
];

const MESSAGE_KEYS = [
  'one_to_one_message',
  'main_menu_message',
  'one_to_one_button_text',
  'ai_chat_button_text',
  'one_to_one_message_once',
  'one_to_one_message_cooldown_hours',
  'main_menu_delay_minutes',
];

const REMINDER_KEYS = [
  'reminder_enabled',
  'reminder_delay_minutes',
  'reminder_message',
  'reminder_stage_id',
];

const PLACEHOLDERS = [
  { token: '{{name}}', hint: 'Customer name, or there when the name is empty' },
  {
    token: '{{brand}}',
    hint: 'Company name, or our store when the company name is empty',
  },
];

const EMPTY_FORM = {
  one_to_one_message: '',
  main_menu_message: '',
  one_to_one_button_text: '',
  ai_chat_button_text: '',
  one_to_one_message_once: false,
  one_to_one_message_cooldown_hours: 1,
  main_menu_delay_minutes: 1,
  reminder_enabled: false,
  reminder_delay_minutes: 0,
  reminder_message: '',
  reminder_stage_id: null,
};

function pickFields(source, keys) {
  return Object.fromEntries(keys.map((key) => [key, source[key]]));
}

function sliceDirty(current, saved, keys) {
  return keys.some((key) => current[key] !== saved[key]);
}

function reminderPayload(formData) {
  const stage = formData.reminder_stage_id;
  return {
    reminder_enabled: Boolean(formData.reminder_enabled),
    reminder_delay_minutes: Number(formData.reminder_delay_minutes),
    reminder_message: String(formData.reminder_message || '').trim(),
    reminder_stage_id: stage == null || stage === '' ? null : Number(stage),
  };
}

function isWhatsAppNotConnected(error) {
  if (error?.response?.status !== 404) return false;
  const data = error.response?.data;
  const detail =
    (typeof data === 'string' && data) || data?.detail || data?.message || '';
  return String(detail).includes('WhatsApp is not connected');
}

function validateReminder(formData) {
  if (!formData.reminder_enabled) return {};
  const minutes = Number(formData.reminder_delay_minutes);
  const next = {};
  if (!Number.isInteger(minutes) || minutes < 1 || minutes > 1440) {
    next.reminder_delay_minutes = 'Enter a time from 1 to 1440 minutes.';
  }
  if (!String(formData.reminder_message || '').trim()) {
    next.reminder_message = 'Enter the reminder message.';
  }
  return next;
}

function formFromSettings(data) {
  const delayMinutes = data.main_menu_delay_minutes ?? 1;
  return {
    one_to_one_message: data.one_to_one_message || '',
    main_menu_message: data.main_menu_message || '',
    one_to_one_button_text: data.one_to_one_button_text || '',
    ai_chat_button_text: data.ai_chat_button_text || '',
    one_to_one_message_once: Boolean(data.one_to_one_message_once),
    one_to_one_message_cooldown_hours:
      data.one_to_one_message_cooldown_hours || 1,
    main_menu_delay_minutes: delayMinutes,
    reminder_enabled: Boolean(data.reminder_enabled),
    reminder_delay_minutes: data.reminder_delay_minutes ?? 0,
    reminder_message: data.reminder_message || '',
    reminder_stage_id:
      data.reminder_stage_id == null ? null : Number(data.reminder_stage_id),
  };
}

function SectionIcon({ name }) {
  const common = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    className: 'h-4 w-4',
    'aria-hidden': true,
  };
  if (name === 'flow') {
    return (
      <svg {...common}>
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" />
        <rect x="8.5" y="14" width="7" height="7" rx="1.5" />
        <path d="M6.5 10v2.5a2 2 0 0 0 2 2H12" />
        <path d="M17.5 10v2.5a2 2 0 0 1-2 2H12" />
      </svg>
    );
  }
  if (name === 'bell') {
    return (
      <svg {...common}>
        <path d="M6 8a6 6 0 0 1 12 0c0 7 3 7 3 9H3c0-2 3-2 3-9" />
        <path d="M10 21a2 2 0 0 0 4 0" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M21 12a8.5 8.5 0 0 1-8.5 8.5c-1.5 0-2.5-.4-3.5-1L4 21l1.2-4.2C4.4 15.7 4 14.5 4 13A8.5 8.5 0 0 1 12.5 4 8.5 8.5 0 0 1 21 12z" />
    </svg>
  );
}

export function WhatsAppSettingsDrawer({ companyId, isOpen, onClose }) {
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const canEdit = can(PERMISSIONS.WA_FLOWS.UPDATE);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [savedSnapshot, setSavedSnapshot] = useState('');
  const [reminderErrors, setReminderErrors] = useState({});
  const [section, setSection] = useState('messages');
  // Separate from the actual value — tracks which UI mode the Main Menu
  // Delay select is in, so a custom value that happens to not match a
  // preset still shows the number input instead of silently snapping back.
  const [isCustomMenuDelay, setIsCustomMenuDelay] = useState(false);
  const formRef = useRef(formData);
  formRef.current = formData;

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['whatsapp-settings', companyId],
    queryFn: () => getWhatsAppSettings(companyId),
    enabled: !!companyId && isOpen,
    retry: (count, err) => !isWhatsAppNotConnected(err) && count < 2,
  });
  const notConnected = isError && isWhatsAppNotConnected(error);
  const sections = notConnected
    ? SECTIONS.filter((item) => item.id !== 'reminder')
    : SECTIONS;

  const { data: stagesData } = useQuery({
    queryKey: ['crm-pipeline-stages', companyId],
    queryFn: () => getCRMPipelineStages(companyId),
    enabled: !!companyId && isOpen && !notConnected,
    staleTime: 60_000,
  });
  const stageOptions = (
    Array.isArray(stagesData)
      ? stagesData
      : stagesData?.results || stagesData?.stages || []
  )
    .slice()
    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
    .map((stage) => ({
      value: String(stage.id),
      label: stage.name,
    }));

  useEffect(() => {
    if (!data) return;
    const next = formFromSettings(data);
    setFormData(next);
    setSavedSnapshot(JSON.stringify(next));
    setReminderErrors({});
    setIsCustomMenuDelay(
      !MAIN_MENU_DELAY_PRESETS.includes(next.main_menu_delay_minutes),
    );
  }, [data]);

  useEffect(() => {
    if (!isOpen) {
      setSection('messages');
    }
  }, [isOpen]);

  const updateMutation = useMutation({
    mutationFn: async ({ messages, reminder }) => {
      if (messages) {
        await updateWhatsAppSettings({ companyId, payload: messages });
      }
      if (reminder) {
        await updateWhatsAppSettings({ companyId, payload: reminder });
      }
    },
    onSuccess: () => {
      setSavedSnapshot(JSON.stringify(formRef.current));
      toast.success('WhatsApp custom messages updated successfully!');
      queryClient.invalidateQueries({
        queryKey: ['whatsapp-settings', companyId],
      });
    },
    onError: (err) => {
      const { message, fieldErrors } = parseApiError(err);
      const next = {};
      if (fieldErrors.reminder_delay_minutes) {
        next.reminder_delay_minutes = String(
          fieldErrors.reminder_delay_minutes,
        );
      }
      if (fieldErrors.reminder_message) {
        next.reminder_message = String(fieldErrors.reminder_message);
      }
      if (fieldErrors.reminder_stage_id) {
        next.reminder_stage_id = String(fieldErrors.reminder_stage_id);
      }
      if (Object.keys(next).length > 0) setSection('reminder');
      setReminderErrors(next);
      toast.error(message || 'Failed to update messages');
    },
  });

  const isDirty = JSON.stringify(formData) !== savedSnapshot;
  const hasEmptyTimeFields =
    formData.main_menu_delay_minutes === '' ||
    formData.main_menu_delay_minutes === null ||
    formData.main_menu_delay_minutes === undefined ||
    formData.one_to_one_message_cooldown_hours === '' ||
    formData.one_to_one_message_cooldown_hours === null ||
    formData.one_to_one_message_cooldown_hours === undefined;

  const requestClose = () => {
    if (updateMutation.isPending) return;
    onClose?.();
  };

  const handleSave = () => {
    if (updateMutation.isPending || !savedSnapshot) return;
    const saved = JSON.parse(savedSnapshot);
    const messagesDirty = sliceDirty(formData, saved, MESSAGE_KEYS);
    const reminderDirty = sliceDirty(formData, saved, REMINDER_KEYS);
    if (reminderDirty) {
      const next = validateReminder(formData);
      if (Object.keys(next).length > 0) {
        setReminderErrors(next);
        setSection('reminder');
        return;
      }
    }
    setReminderErrors({});
    updateMutation.mutate({
      messages: messagesDirty ? pickFields(formData, MESSAGE_KEYS) : null,
      reminder: reminderDirty ? reminderPayload(formData) : null,
    });
  };

  const selectClass =
    'w-full max-w-md h-[40px] sm:h-[44px] flex items-center justify-between rounded-xl bg-input-background border border-border-default text-text-primary text-xs sm:text-sm outline-none focus:border-primary px-3 sm:px-4 transition-all';

  const patch = (partial) => setFormData((prev) => ({ ...prev, ...partial }));

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={requestClose}
      width="settings"
      className="wa-settings-drawer"
      title="WhatsApp Settings"
      description="Messages, menu, and reminders"
      bodyClassName="p-0 overflow-hidden bg-white"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={requestClose} type="button">
            Cancel
          </Button>
          {canEdit && (
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={
                updateMutation.isPending ||
                hasEmptyTimeFields ||
                !isDirty ||
                !savedSnapshot
              }
              className="h-[44px] px-6 text-sm font-medium"
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          )}
        </div>
      }
    >
      {isLoading ? (
        <div className="space-y-6 p-6">
          <SkeletonText width="w-48" height="h-6" />
          <SkeletonText width="w-full" height="h-24" />
          <SkeletonText width="w-full" height="h-24" />
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center gap-3 py-10 text-status-error">
          <p className="text-sm font-semibold">
            {notConnected
              ? 'WhatsApp is not connected for this company.'
              : 'Failed to load WhatsApp settings'}
          </p>
          <Button
            variant="primary"
            onClick={() => refetch()}
            className="px-4 h-8 text-xs"
          >
            Retry
          </Button>
        </div>
      ) : (
        <div className="relative flex min-h-0 flex-1 flex-col lg:flex-row">
          <nav
            aria-label="Settings sections"
            className="shrink-0 border-b border-border-default bg-surface-mint-soft lg:w-[240px] lg:border-b-0 lg:border-r lg:overflow-y-auto"
          >
            <div className="grid grid-cols-3 gap-1 p-2 sm:gap-2 sm:p-3 lg:flex lg:flex-col lg:p-4">
              {sections.map((item) => {
                const active = section === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    aria-current={active ? 'page' : undefined}
                    onClick={() => setSection(item.id)}
                    className={`flex min-w-0 flex-col items-center gap-1 rounded-xl border px-1.5 py-2 text-center outline-none transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-primary/40 sm:flex-row sm:items-start sm:gap-2 sm:px-2.5 sm:py-2.5 sm:text-left lg:gap-3 lg:px-3 ${
                      active
                        ? 'border-primary/30 bg-surface-mint'
                        : 'border-transparent hover:bg-layer-7-hover'
                    }`}
                  >
                    <span
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border sm:mt-0.5 sm:h-8 sm:w-8 ${
                        active
                          ? 'border-primary/20 bg-white text-primary'
                          : 'border-border-default bg-white text-text-muted'
                      }`}
                    >
                      <SectionIcon name={item.icon} />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[11px] font-medium leading-tight text-text-primary sm:text-sm">
                        {item.title}
                      </span>
                      <span className="mt-0.5 hidden text-[11px] leading-snug text-text-muted sm:block">
                        {item.description}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </nav>

          <div className="min-h-0 min-w-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5 sm:py-5">
            <section hidden={section !== 'messages'} className="space-y-0">
              <SectionHeading
                icon="message"
                title="Default Messages"
                text="Greeting for a one-to-one chat."
              />
              <ToggleRow
                title="Send Only Once"
                text="Send the greeting once per customer."
              >
                <Switch
                  checked={formData.one_to_one_message_once}
                  onChange={(checked) =>
                    patch({ one_to_one_message_once: checked })
                  }
                  disabled={!canEdit}
                  aria-label="Send greeting message only once"
                  className="shrink-0"
                />
              </ToggleRow>
              <FieldBlock
                title="One-to-One Message"
                text="Text the customer receives."
              >
                <Textarea
                  value={formData.one_to_one_message}
                  onChange={(e) =>
                    patch({ one_to_one_message: e.target.value })
                  }
                  placeholder="Enter one-to-one message..."
                  disabled={!canEdit}
                  className="min-h-[140px]"
                />
              </FieldBlock>
              <FieldBlock
                title="Greeting Message Cooldown"
                text="Wait before sending the greeting again."
                last
              >
                <CustomSelect
                  value={formData.one_to_one_message_cooldown_hours}
                  onChange={(val) =>
                    patch({ one_to_one_message_cooldown_hours: Number(val) })
                  }
                  options={COOLDOWN_HOUR_OPTIONS}
                  disabled={!canEdit || formData.one_to_one_message_once}
                  buttonClassName={`${selectClass} ${!canEdit || formData.one_to_one_message_once ? 'opacity-60 cursor-not-allowed' : ''}`}
                />
              </FieldBlock>
            </section>

            <section hidden={section !== 'flow'}>
              <SectionHeading
                icon="flow"
                title="Flow Configuration"
                text="Menu message, delay, and button labels."
              />
              <FieldBlock
                title="Main Menu Message"
                text="Shown when the customer picks a flow."
              >
                <Textarea
                  value={formData.main_menu_message}
                  onChange={(e) => patch({ main_menu_message: e.target.value })}
                  placeholder="Enter main menu message..."
                  disabled={!canEdit}
                  className="min-h-[140px]"
                />
              </FieldBlock>
              <FieldBlock
                title="Main Menu Delay"
                text="Wait after the last message before sending the menu."
              >
                <div className="flex max-w-md flex-col gap-2">
                  <CustomSelect
                    value={
                      isCustomMenuDelay
                        ? 'custom'
                        : formData.main_menu_delay_minutes
                    }
                    onChange={(val) => {
                      if (val === 'custom') {
                        setIsCustomMenuDelay(true);
                        return;
                      }
                      setIsCustomMenuDelay(false);
                      patch({ main_menu_delay_minutes: Number(val) });
                    }}
                    options={MAIN_MENU_DELAY_OPTIONS}
                    disabled={!canEdit}
                    buttonClassName={`${selectClass} max-w-none ${!canEdit ? 'opacity-60 cursor-not-allowed' : ''}`}
                  />
                  {isCustomMenuDelay && (
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={1}
                        className="max-w-[140px]"
                        value={formData.main_menu_delay_minutes}
                        onChange={(e) =>
                          patch({
                            main_menu_delay_minutes:
                              e.target.value === ''
                                ? ''
                                : Number(e.target.value),
                          })
                        }
                        disabled={!canEdit}
                        aria-label="Custom main menu delay in minutes"
                      />
                      <span className="shrink-0 text-xs text-text-muted">
                        minutes
                      </span>
                    </div>
                  )}
                </div>
              </FieldBlock>
              <div className="grid grid-cols-1 gap-x-4 border-b border-border-default sm:grid-cols-2">
                <FieldBlock
                  title="One-to-One Button Text"
                  text="19 characters max."
                  last
                >
                  <Input
                    type="text"
                    value={formData.one_to_one_button_text}
                    maxLength={19}
                    onChange={(e) =>
                      patch({ one_to_one_button_text: e.target.value })
                    }
                    placeholder="Enter one-to-one button text..."
                    disabled={!canEdit}
                  />
                </FieldBlock>
                <FieldBlock
                  title="Automation Chat Button Text"
                  text="16 characters max."
                  last
                >
                  <Input
                    type="text"
                    value={formData.ai_chat_button_text}
                    maxLength={16}
                    onChange={(e) =>
                      patch({ ai_chat_button_text: e.target.value })
                    }
                    placeholder="Enter automation chat button text..."
                    disabled={!canEdit}
                  />
                </FieldBlock>
              </div>
            </section>

            {!notConnected && (
              <section hidden={section !== 'reminder'}>
                <SectionHeading
                  icon="bell"
                  title="No-reply reminder"
                  text="One message for every flow. The customer stays on the same question."
                />
                <ReminderBlock
                  formData={formData}
                  canEdit={canEdit}
                  errors={reminderErrors}
                  stageOptions={stageOptions}
                  selectClass={selectClass}
                  onPatch={patch}
                  onClearError={(key) =>
                    setReminderErrors((prev) => ({ ...prev, [key]: undefined }))
                  }
                />
              </section>
            )}
          </div>
        </div>
      )}
    </SideDrawer>
  );
}

function SectionHeading({ icon, title, text }) {
  return (
    <div className="mb-4 flex items-start gap-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border-default bg-surface-mint text-primary">
        <SectionIcon name={icon} />
      </span>
      <div>
        <h3 className="text-sm font-medium text-text-primary">{title}</h3>
        <p className="mt-0.5 text-xs leading-snug text-text-muted">{text}</p>
      </div>
    </div>
  );
}

function ToggleRow({ title, text, children, last = false }) {
  return (
    <div
      className={`flex items-center justify-between gap-4 py-4 ${
        last ? '' : 'border-b border-border-default'
      }`}
    >
      <div className="min-w-0">
        <p className="text-xs font-medium font-sf-pro-display text-text-secondary">
          {title}
        </p>
        {text && (
          <p className="mt-1 max-w-xl text-xs leading-snug text-text-muted">
            {text}
          </p>
        )}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

function ReminderBlock({
  formData,
  canEdit,
  errors,
  stageOptions,
  selectClass,
  onPatch,
  onClearError,
}) {
  const messageRef = useRef(null);
  const enabled = Boolean(formData.reminder_enabled);
  const fieldsLocked = !canEdit || !enabled;
  const minutes = Number(formData.reminder_delay_minutes);
  const [isCustomDelay, setIsCustomDelay] = useState(
    () => minutes > 0 && !REMINDER_DELAY_PRESETS.includes(minutes),
  );

  const insertPlaceholder = (token) => {
    const el = messageRef.current;
    const current = String(formData.reminder_message || '');
    const start = el?.selectionStart ?? current.length;
    const end = el?.selectionEnd ?? start;
    const next = current.slice(0, start) + token + current.slice(end);
    onClearError('reminder_message');
    onPatch({ reminder_message: next });
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const cursor = start + token.length;
      el.setSelectionRange(cursor, cursor);
    });
  };

  return (
    <div className="py-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-text-primary">
            Send a reminder
          </p>
          <p className="mt-1 text-xs leading-snug text-text-muted">
            If the customer does not reply, the server sends this once. They
            stay on the same question. This applies to every flow.
          </p>
        </div>
        <Switch
          checked={enabled}
          onChange={(checked) => {
            onClearError('reminder_delay_minutes');
            onClearError('reminder_message');
            onClearError('reminder_stage_id');
            onPatch({ reminder_enabled: checked });
          }}
          disabled={!canEdit}
          aria-label="Send a reminder"
          className="shrink-0"
        />
      </div>
      <div className={`mt-3 ${fieldsLocked ? 'opacity-60' : ''}`}>
        <label
          htmlFor="reminder_delay_minutes"
          className="text-sm font-medium text-text-primary"
        >
          How long to wait
        </label>
        <p className="mt-1 text-xs leading-snug text-text-muted">
          After a question is sent. Custom time is 24 hours at most.
        </p>
        <div className="mt-2 flex max-w-xs flex-col gap-2">
          <CustomSelect
            value={isCustomDelay ? 'custom' : minutes || ''}
            onChange={(val) => {
              onClearError('reminder_delay_minutes');
              if (val === 'custom') {
                setIsCustomDelay(true);
                return;
              }
              setIsCustomDelay(false);
              onPatch({ reminder_delay_minutes: Number(val) });
            }}
            options={REMINDER_DELAY_OPTIONS}
            disabled={fieldsLocked}
            buttonClassName={`${selectClass} max-w-none ${fieldsLocked ? 'opacity-60 cursor-not-allowed' : ''}`}
          />
          {isCustomDelay && (
            <CustomDelayFields
              minutes={minutes}
              canEdit={!fieldsLocked}
              onChange={(total) => {
                onClearError('reminder_delay_minutes');
                onPatch({ reminder_delay_minutes: total });
              }}
            />
          )}
        </div>
        {enabled && minutes >= 30 && (
          <p className="mt-2 rounded-xl border border-status-warning/40 bg-surface-mint-soft px-3 py-2 text-xs leading-snug text-text-primary">
            The WhatsApp flow session ends after 30 minutes of no customer
            reply, so a delay of 30 or more usually never sends. A shorter delay
            still leaves the rest of that 30 minutes for the customer to answer.
            The reminder itself does not reset that 30-minute clock.
          </p>
        )}
        {errors.reminder_delay_minutes && (
          <p className="mt-1 text-sm font-medium text-status-error">
            {errors.reminder_delay_minutes}
          </p>
        )}
      </div>
      <div className={`mt-3 ${fieldsLocked ? 'opacity-60' : ''}`}>
        <label
          htmlFor="reminder_message"
          className="text-sm font-medium text-text-primary"
        >
          Message
        </label>
        <p className="mt-1 text-xs leading-snug text-text-muted">
          Required while reminders are on. The server sends this text, then the
          same question again, unless the text already includes that question.
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          {PLACEHOLDERS.map((item) => (
            <button
              key={item.token}
              type="button"
              title={item.hint}
              disabled={fieldsLocked}
              onClick={() => insertPlaceholder(item.token)}
              className="rounded-full border border-border-default bg-surface-mint px-2.5 py-1 text-xs font-medium text-primary hover:border-primary disabled:cursor-not-allowed"
            >
              {item.token}
            </button>
          ))}
        </div>
        <p className="mt-1 text-[11px] leading-snug text-text-muted">
          {'{{name}}'} is the customer name, or there when the name is empty.
          {' {{brand}}'} is the company name, or our store when the company name
          is empty.
        </p>
        <Textarea
          ref={messageRef}
          id="reminder_message"
          className="mt-2 min-h-[140px]"
          error={Boolean(errors.reminder_message)}
          value={formData.reminder_message}
          onChange={(e) => {
            onClearError('reminder_message');
            onPatch({ reminder_message: e.target.value });
          }}
          placeholder="Hi {{name}}, please reply to continue."
          disabled={fieldsLocked}
        />
        {errors.reminder_message && (
          <p className="mt-1 text-sm font-medium text-status-error">
            {errors.reminder_message}
          </p>
        )}
      </div>
      <div className={`mt-3 ${fieldsLocked ? 'opacity-60' : ''}`}>
        <label
          htmlFor="reminder_stage_id"
          className="text-sm font-medium text-text-primary"
        >
          Stage after the reminder is sent
        </label>
        <p className="mt-1 text-xs leading-snug text-text-muted">
          Optional. The lead moves only after a successful send, then returns
          when the customer replies.
        </p>
        <div className="mt-2 max-w-xs">
          <CustomSelect
            value={
              formData.reminder_stage_id == null
                ? ''
                : String(formData.reminder_stage_id)
            }
            onChange={(val) => {
              onClearError('reminder_stage_id');
              onPatch({
                reminder_stage_id:
                  val === '' || val == null ? null : Number(val),
              });
            }}
            options={[{ value: '', label: 'No stage change' }, ...stageOptions]}
            disabled={fieldsLocked}
            buttonClassName={`${selectClass} max-w-none ${fieldsLocked ? 'cursor-not-allowed' : ''}`}
          />
        </div>
        {errors.reminder_stage_id && (
          <p className="mt-1 text-sm font-medium text-status-error">
            {errors.reminder_stage_id}
          </p>
        )}
      </div>
    </div>
  );
}

function clampReminderMinutes(hours, mins) {
  const h = Math.min(24, Math.max(0, hours));
  const m = h >= 24 ? 0 : Math.min(59, Math.max(0, mins));
  return Math.min(1440, h * 60 + m);
}

function CustomDelayFields({ minutes, canEdit, onChange }) {
  const safe = Number.isFinite(minutes) && minutes > 0 ? minutes : 0;
  const hours = Math.min(24, Math.floor(safe / 60));
  const mins = hours >= 24 ? 0 : safe % 60;

  const write = (nextHours, nextMins) => {
    onChange(clampReminderMinutes(nextHours, nextMins));
  };

  return (
    <div className="flex items-center gap-3">
      <label className="flex items-center gap-2">
        <Input
          type="number"
          min={0}
          max={24}
          className="max-w-[88px]"
          value={hours}
          onChange={(e) => {
            const raw = e.target.value;
            write(raw === '' ? 0 : Number(raw), mins);
          }}
          onWheel={(e) => e.currentTarget.blur()}
          disabled={!canEdit}
          aria-label="Custom reminder delay in hours"
        />
        <span className="shrink-0 text-xs text-text-muted">hours</span>
      </label>
      <label className="flex items-center gap-2">
        <Input
          type="number"
          min={0}
          max={hours >= 24 ? 0 : 59}
          className="max-w-[88px]"
          value={mins}
          onChange={(e) => {
            const raw = e.target.value;
            write(hours, raw === '' ? 0 : Number(raw));
          }}
          onWheel={(e) => e.currentTarget.blur()}
          disabled={!canEdit || hours >= 24}
          aria-label="Custom reminder delay in minutes"
        />
        <span className="shrink-0 text-xs text-text-muted">min</span>
      </label>
    </div>
  );
}

function FieldBlock({ title, text, children, last = false }) {
  return (
    <div className={`py-4 ${last ? '' : 'border-b border-border-default'}`}>
      <p className="text-sm font-medium text-text-primary">{title}</p>
      {text && (
        <p className="mt-1 max-w-2xl text-xs leading-snug text-text-muted">
          {text}
        </p>
      )}
      <div className="mt-2 min-w-0">{children}</div>
    </div>
  );
}
